import React, { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { Button } from "components"
import CmChipInput from "./CmChipInput"
import { getAutoApplyCoupons, validateCouponForOrders } from "services"
import { getValidateCouponForOrdersRequestData, refactorDiscounts } from "utils"
import classes from "./index.module.scss"

const CheckoutCouponValidation = props => {
  const {
    isOrderEditable,
    discounts,
    setDiscounts,
    deliveryFees,
    orderItems,
    selectedUserAccount
  } = props

  const [t] = useTranslation()

  const cashierBranchId =
    useSelector(state => state.userAccountInfo.branchId)

  const orderForEditDetails =
    useSelector(state => state.orderForEditDetails)

  const isOnline =
    useSelector(state => !state.internetStatus.forceOffline) &&
    !orderForEditDetails?.offlineOrderId

  const {
    deliveryType,
    deliveryAddress
  } = useSelector(state => state.deliveryInfo)

  const [sending, setSending] = useState("")
  const [inputText, setInputText] = useState("")
  const [error, setError] = useState("")

  const timeout = useRef(null)
  const inputRef = useRef(null)

  const validate = () => {
    const isDelivery = deliveryType === "delivery"
    const haveAddress = !!deliveryAddress?._id

    if (isDelivery && !haveAddress) {
      return setError(t("لطفا_ابتدا_آدرس_ارسال_را_وارد_کنید"))
    }

    const value = inputText

    if (!value) return false

    const doesExist =
      discounts.some(discount =>
        discount.couponCode === inputText
      )

    if (doesExist) return false

    return true
  }

  const handleAddCoupon = async (e) => {
    e.preventDefault()

    if (!validate()) return

    setError("")
    setSending(true)

    const data =
      getValidateCouponForOrdersRequestData(
        discounts,
        deliveryFees,
        orderItems,
        deliveryType
      )

    try {
      const response =
        await validateCouponForOrders(
          {
            couponCode: inputText,
            orders: data,
            branchId: cashierBranchId
          },
          {
            accountId: selectedUserAccount?._id || ""
          }
        )

      inputRef.current.style.width = "5px"

      setInputText("")

      const newDiscount = refactorDiscounts(response)

      setDiscounts(prev =>
        [...prev, ...newDiscount]
      )


      newDiscount.length === 0 &&
        setError(t("کوپن_اعمال_نشد"))

    } catch (err) {
      console.log(err)

      setError(t("کوپن_اعمال_نشد"))
    }

    setSending(false)
  }

  const handleDeleteCoupon = async (couponCode) => {
    if (sending) return

    setSending(true)

    try {
      let newDiscounts = []

      const newCouponCodes =
        couponCodes.filter(c => c !== couponCode)

      for (let i = 0; i < newCouponCodes.length; i++) {
        const data =
          getValidateCouponForOrdersRequestData(
            newDiscounts,
            deliveryFees,
            orderItems,
            deliveryType
          )

        const response =
          await validateCouponForOrders(
            {
              couponCode: newCouponCodes[i],
              orders: data,
              branchId: cashierBranchId
            },
            {
              accountId: selectedUserAccount?._id || ""
            }
          )

        const newDiscount = refactorDiscounts(response)

        newDiscounts = [...newDiscounts, ...newDiscount]
      }

      setDiscounts([...newDiscounts])

      setSending(false)
    } catch (err) {
      console.log(err)
    }
  }

  const getOrderCoupons = () => {
    const result = []

    discounts.forEach(discount => {
      discount.couponCanUsed &&
        result.push(discount.couponCode)
    })

    return result
  }

  const revalidateCoupons = async (couponCodes, accountId) => {
    setSending(true)

    let newDiscounts = []

    for (let i = 0; i < couponCodes.length; i++) {
      const data =
        getValidateCouponForOrdersRequestData(
          newDiscounts,
          deliveryFees,
          orderItems,
          deliveryType
        )

      const response =
        await validateCouponForOrders(
          {
            orders: data,
            couponCode: couponCodes[i],
            branchId: cashierBranchId
          },
          {
            accountId
          }
        )

      const newDiscount = refactorDiscounts(response)

      newDiscounts = [...newDiscounts, ...newDiscount]
    }

    setDiscounts([...newDiscounts])

    setSending(false)
  }

  const revalidateCouponsDebouncer = async () => {
    clearTimeout(timeout.current)

    if (couponCodes.length === 0) return

    const accountId = selectedUserAccount?._id || ""

    timeout.current =
      setTimeout(() =>
        revalidateCoupons(
          couponCodes,
          accountId
        ),
        700
      )
  }

  const addAutoApplyCoupons = async () => {
    if (!isOnline) return

    const newDiscounts = []

    try {
      const autoApplyCoupons =
        await getAutoApplyCoupons({ branchId: cashierBranchId })

      for (const autoApplyCoupon of autoApplyCoupons) {
        const data =
          getValidateCouponForOrdersRequestData(
            newDiscounts,
            deliveryFees,
            orderItems,
            deliveryType
          )

        const response =
          await validateCouponForOrders(
            {
              orders: data,
              branchId: cashierBranchId,
              couponCode: autoApplyCoupon.code
            },
            {
              accountId: selectedUserAccount?._id
            }
          )

        const isAutoApply = true

        const newDiscount =
          refactorDiscounts(response, isAutoApply)

        setDiscounts(prev => {
          const filteredDiscounts =
            newDiscount.filter(d =>
              !prev.some(dis => dis.couponCode === d.couponCode)
            )

          return [...prev, ...filteredDiscounts]
        })
      }
    } catch (err) {
      console.log(err)
    }
  }

  const validatePreviousCouponCodes = () => {
    if (!orderForEditDetails) return

    const accountId =
      orderForEditDetails.selectedUserAccount?._id || ""

    revalidateCoupons(
      orderForEditDetails.couponCodes,
      accountId
    )
  }

  useEffect(() => {
    revalidateCouponsDebouncer()
  }, [
    deliveryType,
    deliveryFees,
    selectedUserAccount
  ])

  useEffect(() => {
    validatePreviousCouponCodes()

    addAutoApplyCoupons()

    return () => clearTimeout(timeout.current)
  }, [])

  const couponCodes = getOrderCoupons()

  const disabled = !isOnline || !isOrderEditable

  return (
    <div className={classes["container"]}>
      <CmChipInput
        couponCodes={couponCodes}
        setDiscounts={setDiscounts}
        error={error}
        inputRef={inputRef}
        inputText={inputText}
        setInputText={setInputText}
        onDeleteCoupon={handleDeleteCoupon}
        handleAddCoupon={handleAddCoupon}
        disabled={disabled}
      />
      <div className={classes["button"]}>
        <Button
          fullWidth
          color="primary"
          loading={sending}
          onClick={handleAddCoupon}
        >
          {t("اعمال_کوپن")}
        </Button>
      </div>
    </div>
  )
}

export default React.memo(CheckoutCouponValidation)